/* NYLA Makeup Engine — WebGL renderer for photoreal matte makeup.
 *
 * Renders the camera/photo + makeup in a 3-stage GPU pipeline:
 *   1) MASK pass (half res FBO):  R = lip coverage (smoothed annulus mesh between
 *      the outer & inner lip contours — the mouth interior is never painted),
 *      G = blush coverage (analytic soft ellipses on the cheekbones),
 *      B = scene luminance (used as the local-average light map).
 *   2) BLUR pass (separable gaussian): feathers the lip edge and widens the
 *      luminance map so it represents the *neighbourhood* average light.
 *   3) COMPOSITE pass (full res): re-lights the shade colour with the lip's own
 *      micro-texture:  ratio = pixelLuma / localAvgLuma  → creases keep their
 *      depth, specular shine is crushed (matte), and the shade hue stays true
 *      on every skin tone (no muddy multiply).
 *
 * The engine is UI-agnostic: feed it a <video>/<img> + face landmarks each
 * frame and it draws. Exposed as window.NylaMakeup.Engine (no build step).
 */
(function () {
  'use strict';

  // MediaPipe FaceMesh lip contours — ordered closed loops, index-aligned
  // (outer[i] sits opposite inner[i]; both start at the left corner).
  var LIPS_OUTER = [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146];
  var LIPS_INNER = [78, 191, 80, 81, 82, 13, 312, 311, 310, 415, 308, 324, 318, 402, 317, 14, 87, 178, 88, 95];
  var CHEEK_A = [50, 101, 118, 117];     // cheek apple cluster (one side)
  var CHEEK_B = [280, 330, 347, 346];    // other side
  var SIDE_A = 234, SIDE_B = 454;        // face-oval points by the ears
  var UP_TOP = 168, UP_BOT = 152;        // nose bridge → chin = face "up" axis

  var SUB = 3; // Catmull-Rom subdivisions per lip segment (20 pts → 60 pts/ring)
  var RING = LIPS_OUTER.length * SUB;

  // ── Shaders (GLSL ES 1.0 — works on WebGL1 and 2) ─────────────────────────

  var VS_GEOM = [
    'attribute vec2 a_pos;',            // video-pixel coords
    'attribute vec2 a_loc;',            // local ellipse coords (blush only)
    'uniform vec2 u_res;',
    'varying vec2 v_loc;',
    'void main(){',
    '  v_loc = a_loc;',
    '  vec2 c = a_pos / u_res * 2.0 - 1.0;',
    '  gl_Position = vec4(c.x, -c.y, 0.0, 1.0);', // image y-down → clip
    '}'
  ].join('\n');

  var FS_LIP = [
    'precision mediump float;',
    'void main(){ gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0); }'
  ].join('\n');

  var FS_BLUSH = [
    'precision mediump float;',
    'varying vec2 v_loc;',
    'uniform float u_soft;',            // falloff exponent
    'void main(){',
    '  float r2 = dot(v_loc, v_loc);',
    '  float a = pow(clamp(1.0 - r2, 0.0, 1.0), u_soft);', // soft dome: flat core, long tail
    '  gl_FragColor = vec4(0.0, a, 0.0, 1.0);',
    '}'
  ].join('\n');

  var VS_QUAD = [
    'attribute vec2 a_pos;',            // clip-space fullscreen quad
    'varying vec2 v_uv;',
    'void main(){',
    '  v_uv = a_pos * 0.5 + 0.5;',      // v=0 at clip bottom
    '  gl_Position = vec4(a_pos, 0.0, 1.0);',
    '}'
  ].join('\n');

  // Copies scene luminance into the B channel of the mask FBO.
  // The FBO is written y-flipped relative to the video texture (geometry pass
  // flips y), so sample the video flipped here to match.
  var FS_LUMA = [
    'precision mediump float;',
    'varying vec2 v_uv;',
    'uniform sampler2D u_tex;',
    'void main(){',
    '  vec3 c = texture2D(u_tex, vec2(v_uv.x, 1.0 - v_uv.y)).rgb;',
    '  float l = dot(c, vec3(0.299, 0.587, 0.114));',
    '  gl_FragColor = vec4(0.0, 0.0, l, 1.0);',
    '}'
  ].join('\n');

  // Separable gaussian. R/G (masks) use one spread, B (luma map) its own wider
  // spread so the texture-detail base stays stable even with a tight feather.
  var FS_BLUR = [
    'precision mediump float;',
    'varying vec2 v_uv;',
    'uniform sampler2D u_tex;',
    'uniform vec2 u_dirM;',             // direction * feather step (masks)
    'uniform vec2 u_dirL;',             // direction * luma step
    'const float w0 = 0.227027; const float w1 = 0.316216; const float w2 = 0.070270;',
    'const float o1 = 1.384615; const float o2 = 3.230769;',
    'void main(){',
    '  vec2 m = texture2D(u_tex, v_uv).rg * w0;',
    '  float l = texture2D(u_tex, v_uv).b * w0;',
    '  m += texture2D(u_tex, v_uv + u_dirM * o1).rg * w1;',
    '  m += texture2D(u_tex, v_uv - u_dirM * o1).rg * w1;',
    '  m += texture2D(u_tex, v_uv + u_dirM * o2).rg * w2;',
    '  m += texture2D(u_tex, v_uv - u_dirM * o2).rg * w2;',
    '  l += texture2D(u_tex, v_uv + u_dirL * o1).b * w1;',
    '  l += texture2D(u_tex, v_uv - u_dirL * o1).b * w1;',
    '  l += texture2D(u_tex, v_uv + u_dirL * o2).b * w2;',
    '  l += texture2D(u_tex, v_uv - u_dirL * o2).b * w2;',
    '  gl_FragColor = vec4(m, l, 1.0);',
    '}'
  ].join('\n');

  var FS_COMPOSITE = [
    'precision mediump float;',
    'varying vec2 v_uv;',               // v=0 at screen bottom
    'uniform sampler2D u_video;',
    'uniform sampler2D u_mask;',
    'uniform float u_mirror;',
    'uniform float u_divider;',         // compare: makeup only right of this x
    'uniform float u_fade;',            // face-found fade
    'uniform vec3  u_lipColor;  uniform float u_lipInt;  uniform float u_lipOn;',
    'uniform vec3  u_blushColor;uniform float u_blushInt;uniform float u_blushOn;',
    'uniform float u_matte;',           // 0..1 highlight suppression
    'uniform float u_edgeLo; uniform float u_edgeHi;',  // lip mask shaping
    'uniform float u_detail;',          // texture pass-through strength
    '',
    'vec3 lin(vec3 c){ return c * c; }',
    'vec3 srgb(vec3 c){ return sqrt(max(c, 0.0)); }',
    'float luma(vec3 c){ return dot(c, vec3(0.299, 0.587, 0.114)); }',
    '',
    'void main(){',
    '  vec2 suv = vec2(v_uv.x, 1.0 - v_uv.y);',          // screen uv, y-down
    '  vec2 iuv = suv;',
    '  if (u_mirror > 0.5) iuv.x = 1.0 - iuv.x;',        // image-space uv
    '  vec3 src = texture2D(u_video, iuv).rgb;',
    '  vec3 base = lin(src);',
    '',
    '  vec3 m = texture2D(u_mask, vec2(iuv.x, 1.0 - iuv.y)).rgb;',
    '  float lipM = smoothstep(u_edgeLo, u_edgeHi, m.r);',
    '  float blushM = m.g;',
    '  float avgL = m.b;',
    '',
    '  float on = step(u_divider, suv.x) * u_fade;',
    '',
    '  // ── Matte lipstick (Maybelline-style): shade re-lit by the lip\'s own',
    '  //    form + micro-texture. ratio = local luminance / neighbourhood avg.',
    '  float pix = luma(src);',
    '  float avg = max(avgL, 0.04);',
    '  float ratio = pix / avg;',                         // 1 = locally average
    '  float dev = ratio - 1.0;',
    // keep the full shadow gradient (lip form / vermilion edge depth), but
    // crush the bright side so it reads matte (no glossy hotspot).
    '  float shade = 1.0 + (dev >= 0.0 ? dev * (1.0 - u_matte * 0.8) : dev);',
    // amplify deviations so the pout, creases and lip lines stay visible…
    '  shade = 1.0 + (shade - 1.0) * (0.8 + u_detail * 0.7);',
    // …then add a touch of the raw high-frequency detail for crisp texture.
    '  shade += (pix - avg) * u_detail * 0.5;',
    '  shade = clamp(shade, 0.14, 1.8);',
    '  vec3 lip = lin(u_lipColor) * (shade * shade);',   // square ≈ sRGB→linear
    '  float la = lipM * u_lipInt * u_lipOn * on;',
    '  base = mix(base, lip, la);',
    '',
    '  // ── Matte blush: hue-shifting tint that keeps the skin texture ──',
    '  vec3 bl = lin(u_blushColor);',
    '  vec3 nb = bl / max(max(bl.r, max(bl.g, bl.b)), 0.001);',
    '  vec3 tint = base * nb;',                          // multiply by normalised hue
    '  vec3 pig = mix(tint, bl, 0.28);',                 // pigment opacity build
    '  float ba = blushM * u_blushInt * u_blushOn * on * 0.62;',
    '  base = mix(base, pig, ba);',
    '',
    '  gl_FragColor = vec4(srgb(base), 1.0);',
    '}'
  ].join('\n');

  // ── Small GL helpers ───────────────────────────────────────────────────────

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      throw new Error('Shader: ' + gl.getShaderInfoLog(s));
    }
    return s;
  }

  function program(gl, vs, fs) {
    var p = gl.createProgram();
    gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
    gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error('Program: ' + gl.getProgramInfoLog(p));
    }
    var u = {}, n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < n; i++) {
      var info = gl.getActiveUniform(p, i);
      u[info.name] = gl.getUniformLocation(p, info.name);
    }
    return { prog: p, u: u, a_pos: gl.getAttribLocation(p, 'a_pos'), a_loc: gl.getAttribLocation(p, 'a_loc') };
  }

  function makeFBO(gl, w, h) {
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    var fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { fbo: fbo, tex: tex, w: w, h: h };
  }

  // Closed-loop Catmull-Rom resampling → smooth lip line / crisp cupid's bow.
  function resampleRing(pts, sub, out) {
    var n = pts.length, k = 0;
    for (var i = 0; i < n; i++) {
      var p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
      for (var j = 0; j < sub; j++) {
        var t = j / sub, t2 = t * t, t3 = t2 * t;
        out[k++] = 0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);
        out[k++] = 0.5 * (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3);
      }
    }
  }

  function hexToRgb01(hex) {
    var h = (hex || '#C62A33').replace('#', '');
    return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
  }

  // ── Engine ────────────────────────────────────────────────────────────────

  function Engine(canvas) {
    var gl = canvas.getContext('webgl', { alpha: false, antialias: true, preserveDrawingBuffer: true, premultipliedAlpha: true })
          || canvas.getContext('experimental-webgl', { alpha: false, antialias: true, preserveDrawingBuffer: true });
    if (!gl) throw new Error('WebGL is not available');
    this.gl = gl;
    this.canvas = canvas;

    this.pLip = program(gl, VS_GEOM, FS_LIP);
    this.pBlush = program(gl, VS_GEOM, FS_BLUSH);
    this.pLuma = program(gl, VS_QUAD, FS_LUMA);
    this.pBlur = program(gl, VS_QUAD, FS_BLUR);
    this.pComp = program(gl, VS_QUAD, FS_COMPOSITE);

    // Fullscreen quad
    this.quadBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    // Lip annulus mesh: RING outer + RING inner verts, quads between the rings.
    this.lipVerts = new Float32Array(RING * 4);
    this.lipBuf = gl.createBuffer();
    var idx = [];
    for (var i = 0; i < RING; i++) {
      var o0 = i, o1 = (i + 1) % RING, i0 = RING + i, i1 = RING + (i + 1) % RING;
      idx.push(o0, i0, o1, o1, i0, i1);
    }
    this.lipIdx = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.lipIdx);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);
    this.lipIdxCount = idx.length;

    // Blush: two quads (pos + local ellipse coords interleaved)
    this.blushVerts = new Float32Array(2 * 4 * 4);
    this.blushBuf = gl.createBuffer();
    this.blushIdx = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.blushIdx);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 2, 1, 3, 4, 5, 6, 6, 5, 7]), gl.STATIC_DRAW);

    this.videoTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    this.fboA = null; this.fboB = null; this.fboC = null;
    this.W = 0; this.H = 0;

    this.source = null;
    this.landmarks = null;
    this.mirror = false;
    this.divider = 0;
    this.fade = 0;

    this.lip = null;     // { color:[r,g,b], intensity }
    this.blush = null;

    // Render tuning (admin-adjustable; defaults tuned on test portraits).
    this.tuning = {
      featherPx: 0.95,   // lip edge feather, fraction of lip height (×0.06)
      dilate: 1.0,       // outer-lip dilation (1.0 = hug MediaPipe lip border; >1 bleeds onto skin/beard)
      matte: 0.7,        // highlight crush (1=dead flat sticker, lower keeps lifelike lip dimension)
      detail: 1.2,       // lip texture / crease pass-through
      edgeLo: 0.12, edgeHi: 0.72,
      blushSize: 1.0, blushSoft: 2.0, blushAspect: 0.58,
      blushShiftX: 0.0, blushShiftY: 0.0, // fractions of cheek-ear distance
      lumaScale: 3.2     // luma-map blur ÷ mask blur
    };

    this._ring = new Float32Array(RING * 2);
    this._raw = [];
    for (var r = 0; r < 20; r++) this._raw.push({ x: 0, y: 0 });
  }

  Engine.prototype.setSource = function (el, w, h) {
    this.source = el;
    if (w !== this.W || h !== this.H) {
      this.W = w; this.H = h;
      this.canvas.width = w; this.canvas.height = h;
      var gl = this.gl, hw = Math.max(2, w >> 1), hh = Math.max(2, h >> 1);
      this.fboA = makeFBO(gl, hw, hh);
      this.fboB = makeFBO(gl, hw, hh);
      this.fboC = makeFBO(gl, hw, hh);
    }
  };

  Engine.prototype.setLandmarks = function (lm) { this.landmarks = lm || null; };
  Engine.prototype.setMirror = function (m) { this.mirror = !!m; };
  Engine.prototype.setDivider = function (x) { this.divider = x || 0; };
  Engine.prototype.setFade = function (f) { this.fade = f; };

  Engine.prototype.setLip = function (shade) {
    this.lip = shade ? { color: Array.isArray(shade.color) ? shade.color : hexToRgb01(shade.color), intensity: shade.intensity } : null;
  };
  Engine.prototype.setBlush = function (shade) {
    this.blush = shade ? { color: Array.isArray(shade.color) ? shade.color : hexToRgb01(shade.color), intensity: shade.intensity } : null;
  };
  Engine.prototype.setTuning = function (t) {
    for (var k in t) if (Object.prototype.hasOwnProperty.call(this.tuning, k)) this.tuning[k] = t[k];
  };

  // Geometry from landmarks (video-pixel space) ------------------------------

  Engine.prototype._lipGeometry = function (lm) {
    var W = this.W, H = this.H, raw = this._raw, i;

    // Outer ring, dilated slightly from the lip centroid for full coverage.
    var cx = 0, cy = 0;
    for (i = 0; i < 20; i++) { cx += lm[LIPS_OUTER[i]].x; cy += lm[LIPS_OUTER[i]].y; }
    cx /= 20; cy /= 20;
    var g = this.tuning.dilate;
    for (i = 0; i < 20; i++) {
      var p = lm[LIPS_OUTER[i]];
      raw[i].x = (cx + (p.x - cx) * g) * W;
      raw[i].y = (cy + (p.y - cy) * g) * H;
    }
    resampleRing(raw, SUB, this._ring);
    this.lipVerts.set(this._ring, 0);

    for (i = 0; i < 20; i++) { raw[i].x = lm[LIPS_INNER[i]].x * W; raw[i].y = lm[LIPS_INNER[i]].y * H; }
    resampleRing(raw, SUB, this._ring);
    this.lipVerts.set(this._ring, RING * 2);

    // Lip height in px drives the feather + luma-map scale.
    this.lipH = Math.abs(lm[0].y - lm[17].y) * H;
  };

  Engine.prototype._blushGeometry = function (lm) {
    var W = this.W, H = this.H, t = this.tuning, v = this.blushVerts;
    var fwx = (lm[SIDE_B].x - lm[SIDE_A].x) * W, fwy = (lm[SIDE_B].y - lm[SIDE_A].y) * H;
    var faceW = Math.sqrt(fwx * fwx + fwy * fwy) || 1;
    var upx = (lm[UP_TOP].x - lm[UP_BOT].x) * W, upy = (lm[UP_TOP].y - lm[UP_BOT].y) * H;
    var ul = Math.sqrt(upx * upx + upy * upy) || 1; upx /= ul; upy /= ul;

    // The cheek apple from stable anchors (eye corner / mouth corner / nose
    // tip); the ear-side landmark only sets the sweep direction, not the size.
    function side(eyeI, mouthI, sideI, base) {
      var ex = lm[eyeI].x * W, ey = lm[eyeI].y * H;
      var mx2 = lm[mouthI].x * W, my2 = lm[mouthI].y * H;
      var nx = lm[1].x * W, ny = lm[1].y * H;
      var sx = lm[sideI].x * W, sy = lm[sideI].y * H;
      var cx = 0.45 * ex + 0.35 * mx2 + 0.20 * nx;
      var cy = 0.45 * ey + 0.35 * my2 + 0.20 * ny;
      var dx = (0.8 * sx + 0.2 * ex) - cx, dy = (0.8 * sy + 0.2 * ey) - cy;
      var dl = Math.sqrt(dx * dx + dy * dy) || 1; dx /= dl; dy /= dl; // apple → upper ear
      cx += dx * faceW * (0.085 + t.blushShiftX) - upx * faceW * t.blushShiftY;
      cy += dy * faceW * (0.085 + t.blushShiftX) - upy * faceW * t.blushShiftY;
      var a = faceW * 0.155 * t.blushSize;
      var b = a * t.blushAspect;
      var px = -dy, py = dx;                              // minor axis
      var M = 1.45;                                       // quad margin
      var k = base * 16;
      var corners = [[-M, -M], [M, -M], [-M, M], [M, M]];
      for (var i = 0; i < 4; i++) {
        var u = corners[i][0], w2 = corners[i][1];
        v[k++] = cx + dx * a * u + px * b * w2;
        v[k++] = cy + dy * a * u + py * b * w2;
        v[k++] = u; v[k++] = w2;
      }
    }
    side(33, 61, SIDE_A, 0);
    side(263, 291, SIDE_B, 1);
  };

  // Render ------------------------------------------------------------------

  Engine.prototype.render = function () {
    var gl = this.gl;
    if (!this.source || !this.W) return;

    // Upload the current frame.
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.source); } catch (e) { return; }

    var lm = this.landmarks;
    var hasFace = !!lm;
    var wantsMakeup = (this.lip || this.blush) && hasFace && this.fade > 0.01;

    if (wantsMakeup) {
      this._lipGeometry(lm);
      if (this.blush) this._blushGeometry(lm);
      this._maskPass();
      this._blurPass();
    }
    this._compositePass(wantsMakeup);
  };

  Engine.prototype._maskPass = function () {
    var gl = this.gl, fbo = this.fboA;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.fbo);
    gl.viewport(0, 0, fbo.w, fbo.h);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Scene luminance → B
    gl.colorMask(false, false, true, true);
    gl.useProgram(this.pLuma.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(this.pLuma.a_pos);
    gl.vertexAttribPointer(this.pLuma.a_pos, 2, gl.FLOAT, false, 0, 0);
    gl.uniform1i(this.pLuma.u.u_tex, 0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Lip annulus → R
    if (this.lip) {
      gl.colorMask(true, false, false, true);
      gl.useProgram(this.pLip.prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.lipBuf);
      gl.bufferData(gl.ARRAY_BUFFER, this.lipVerts, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(this.pLip.a_pos);
      gl.vertexAttribPointer(this.pLip.a_pos, 2, gl.FLOAT, false, 0, 0);
      gl.uniform2f(this.pLip.u.u_res, this.W, this.H);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.lipIdx);
      gl.drawElements(gl.TRIANGLES, this.lipIdxCount, gl.UNSIGNED_SHORT, 0);
    }

    // Blush ellipses → G
    if (this.blush) {
      gl.colorMask(false, true, false, true);
      gl.useProgram(this.pBlush.prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.blushBuf);
      gl.bufferData(gl.ARRAY_BUFFER, this.blushVerts, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(this.pBlush.a_pos);
      gl.vertexAttribPointer(this.pBlush.a_pos, 2, gl.FLOAT, false, 16, 0);
      gl.enableVertexAttribArray(this.pBlush.a_loc);
      gl.vertexAttribPointer(this.pBlush.a_loc, 2, gl.FLOAT, false, 16, 8);
      gl.uniform2f(this.pBlush.u.u_res, this.W, this.H);
      gl.uniform1f(this.pBlush.u.u_soft, this.tuning.blushSoft);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.blushIdx);
      gl.drawElements(gl.TRIANGLES, 12, gl.UNSIGNED_SHORT, 0);
      gl.disableVertexAttribArray(this.pBlush.a_loc);
    }

    gl.colorMask(true, true, true, true);
  };

  Engine.prototype._blurPass = function () {
    var gl = this.gl;
    // Feather scales with the on-screen lip size → consistent at any distance.
    var featherFull = Math.max(1.5, Math.min(14, (this.lipH || 40) * 0.055 * this.tuning.featherPx));
    var fm = featherFull * 0.5 / 2.3;       // gaussian step in half-res px
    var fl = fm * this.tuning.lumaScale;

    var passes = [
      { src: this.fboA, dst: this.fboB, dx: 1, dy: 0 },
      { src: this.fboB, dst: this.fboC, dx: 0, dy: 1 }
    ];
    gl.useProgram(this.pBlur.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(this.pBlur.a_pos);
    gl.vertexAttribPointer(this.pBlur.a_pos, 2, gl.FLOAT, false, 0, 0);
    gl.uniform1i(this.pBlur.u.u_tex, 0);
    for (var i = 0; i < 2; i++) {
      var p = passes[i];
      gl.bindFramebuffer(gl.FRAMEBUFFER, p.dst.fbo);
      gl.viewport(0, 0, p.dst.w, p.dst.h);
      gl.bindTexture(gl.TEXTURE_2D, p.src.tex);
      gl.uniform2f(this.pBlur.u.u_dirM, p.dx * fm / p.src.w, p.dy * fm / p.src.h);
      gl.uniform2f(this.pBlur.u.u_dirL, p.dx * fl / p.src.w, p.dy * fl / p.src.h);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
  };

  Engine.prototype._compositePass = function (withMakeup) {
    var gl = this.gl, t = this.tuning;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.W, this.H);
    gl.disable(gl.BLEND);
    gl.useProgram(this.pComp.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(this.pComp.a_pos);
    gl.vertexAttribPointer(this.pComp.a_pos, 2, gl.FLOAT, false, 0, 0);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    gl.uniform1i(this.pComp.u.u_video, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, (withMakeup ? this.fboC : this.fboA || this.fboC || {}).tex || this.videoTex);
    gl.uniform1i(this.pComp.u.u_mask, 1);
    gl.activeTexture(gl.TEXTURE0);

    gl.uniform1f(this.pComp.u.u_mirror, this.mirror ? 1 : 0);
    gl.uniform1f(this.pComp.u.u_divider, this.divider);
    gl.uniform1f(this.pComp.u.u_fade, withMakeup ? this.fade : 0);

    var lip = this.lip, bl = this.blush;
    gl.uniform3f(this.pComp.u.u_lipColor, lip ? lip.color[0] : 0, lip ? lip.color[1] : 0, lip ? lip.color[2] : 0);
    gl.uniform1f(this.pComp.u.u_lipInt, lip ? lip.intensity : 0);
    gl.uniform1f(this.pComp.u.u_lipOn, lip ? 1 : 0);
    gl.uniform3f(this.pComp.u.u_blushColor, bl ? bl.color[0] : 0, bl ? bl.color[1] : 0, bl ? bl.color[2] : 0);
    gl.uniform1f(this.pComp.u.u_blushInt, bl ? bl.intensity : 0);
    gl.uniform1f(this.pComp.u.u_blushOn, bl ? 1 : 0);
    gl.uniform1f(this.pComp.u.u_matte, t.matte);
    gl.uniform1f(this.pComp.u.u_detail, t.detail);
    gl.uniform1f(this.pComp.u.u_edgeLo, t.edgeLo);
    gl.uniform1f(this.pComp.u.u_edgeHi, t.edgeHi);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  Engine.prototype.snapshot = function () {
    return this.canvas.toDataURL('image/jpeg', 0.92);
  };

  window.NylaMakeup = window.NylaMakeup || {};
  window.NylaMakeup.Engine = Engine;
  window.NylaMakeup.hexToRgb01 = hexToRgb01;
})();
